import { Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, Optional, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldControl } from '@angular/material/form-field';

import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { AirPort, AirPortService, Country } from '@core/api';
import { SharedModule } from '@shared/modules';

@Component({
  selector: 'net-select-air-port',
  templateUrl: './select-air-port.component.html',
  styleUrls: ['./select-air-port.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectAirPortComponent
    }
  ],
  standalone: true,
  imports: [
    SharedModule
  ]
})
export class SelectAirPortComponent implements OnDestroy, DoCheck, ControlValueAccessor, MatFormFieldControl<AirPort | null> {

  static nextId = 0;

  loading = false;
  focused = false;
  errorState = false;
  controlType = 'select-air-port';
  describedBy = '';
  stateChanges = new Subject<void>();

  airPorts: AirPort[] = [];
  inputCtrl = new UntypedFormControl();

  @ViewChild('auto') auto: MatAutocomplete;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @Input() tabIndex;

  @Input()
  get country() { return this._country; }
  set country(country: Country) {
    if (this._country?.countryId !== country?.countryId) {
      this.reset(!!this._country && !!country);
    }

    this._country = country;
    if (country) {
      this.loading = true;
      this.inputCtrl.disable();
      this.initDefaultOptions().finally(() => {
        this.loading = false;
        if (!this.disabled) {
          this.inputCtrl.enable();
        }
      });
    }
  }
  private _country: Country;

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    if (value) {
      this.inputCtrl.disable();
    } else {
      this.inputCtrl.enable();
    }

    this._disabled = value;
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): AirPort | null { return this._value; }
  set value(value: AirPort | null) {
    this._value = value;
    this.onChange(value);
    this.stateChanges.next();
  }
  private _value: AirPort;

  @HostBinding('id') id = `select-air-port-${SelectAirPortComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedByBinding = this.describedBy;

  get shouldLabelFloat() { return !!this.country && this.focused || !this.empty; }

  get empty() { return !this.value; }

  private initDefaultOptions(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.airPortService.search({
        filter: {
          countryId: this.country.countryId,
        },
        pageSize: 100
      }).toPromise()
        .then((response) => this.airPorts = response.data.results)
        .finally(() => resolve());
    });
  }

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private airPortService: AirPortService,
    @Optional() @Self() public ngControl: NgControl
  ) {
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.disabled) {
        return;
      }

      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    // Listen input control value changes and filter results
    this.inputCtrl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter(value => this.country && typeof value === 'string'),
      tap(() => {
        this.loading = true;
        this.airPorts = [];
        this.value = null;
      }),
      switchMap(value => this.airPortService.search({
        filter: {
          countryId: this.country.countryId,
          name: value
        },
        pageSize: 100
      })),
      tap(() => this.loading = false)
    ).subscribe((response) => this.airPorts = response.data.results);
  }

  displayName = (port: AirPort) => port ? port.name : '';

  reset(force = false) {
    if (!force && this.inputCtrl.pristine) {
      return;
    }

    this.value = null;
    this.airPorts = [];
    this.inputCtrl.setValue(null);
    this.inputCtrl.markAsPristine();

    if (!force && this.country) {
      this.initDefaultOptions();
    }
  }

  onSelect(event: MatAutocompleteSelectedEvent) {
    this.value = event.option.value;
    this.input.nativeElement?.blur();
  }

  onBlur() {
    // On blur with nothing selected reset component
    if (this.ngControl.dirty && !this.auto.isOpen && !this.value) {
      this.reset();
    }
  }

  onClosed() {
    // On panel close with nothing selected reset component
    if (this.ngControl.dirty && !this.value && !this.loading && this.airPorts.length !== 0) {
      this.reset();
    }
  }

  onChange = (_: any) => { };
  onTouched = () => { };
  onContainerClick = () => { };
  registerOnChange = (fn: any) => this.onChange = fn;
  registerOnTouched = (fn: any) => this.onTouched = fn;
  setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;
  setDescribedByIds = (ids: string[]) => this.describedBy = ids.join(' ');

  writeValue(value: AirPort) {
    this.value = value;
    this.inputCtrl.setValue(value);
  }

  ngOnDestroy = () => this.stateChanges.complete();

  ngDoCheck() {
    // Reflect control valid status for mat form field error state
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }
}
